import React from 'react';
import { Box, Typography } from '@mui/material';
import { Treemap, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import ChartCard from './ChartCard';
import { COLORS } from './constants';
import { CategoryDataItem } from './types';

// ---------------------------------------------------------------------------
// Treemap cell renderer
// ---------------------------------------------------------------------------

const TreemapCustomContent: React.FC<any> = ({ x, y, width, height, name, value, index }) => {
  if (!name || width < 4 || height < 4) return null;
  const fill = COLORS[(index ?? 0) % COLORS.length];
  const showLabel = width > 60 && height > 36;
  const showValue = width > 60 && height > 52;

  return (
    <g>
      <rect x={x} y={y} width={width} height={height} fill={fill} stroke="#fff" strokeWidth={2} rx={4} ry={4} />
      {showLabel && (
        <text x={x + width / 2} y={y + height / 2 - (showValue ? 8 : 0)} textAnchor="middle" dominantBaseline="central" fill="#fff" fontSize={Math.min(14, width / 8)} fontWeight={600}>
          {name}
        </text>
      )}
      {showValue && (
        <text x={x + width / 2} y={y + height / 2 + 12} textAnchor="middle" dominantBaseline="central" fill="rgba(255,255,255,0.85)" fontSize={Math.min(12, width / 10)}>
          ₹{Number(value).toLocaleString()}
        </text>
      )}
    </g>
  );
};

// ---------------------------------------------------------------------------
// Custom tooltip with breakdown
// ---------------------------------------------------------------------------

const TreemapCustomTooltip: React.FC<any> = ({ active, payload }) => {
  if (!active || !payload || payload.length === 0) return null;
  const data = payload[0]?.payload;
  if (!data) return null;
  const { name, value, percentage, items } = data;
  const maxItems = 8;
  const displayItems = items?.slice(0, maxItems) ?? [];
  const remaining = (items?.length ?? 0) - maxItems;

  return (
    <Box
      sx={{
        backgroundColor: 'rgba(15, 23, 42, 0.95)',
        borderRadius: '10px',
        p: 1.5,
        minWidth: 200,
        maxWidth: 280,
        boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
        border: '1px solid rgba(255,255,255,0.1)',
      }}
    >
      <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 14, mb: 0.5 }}>
        {name}
      </Typography>
      <Typography sx={{ color: 'rgba(255,255,255,0.9)', fontSize: 13, mb: 0.25 }}>
        Revenue: <strong>₹{Number(value).toLocaleString()}</strong>
      </Typography>
      <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, mb: 1 }}>
        Share: <strong>{percentage}%</strong> of total
      </Typography>
      {displayItems.length > 0 && (
        <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.15)', pt: 0.75, mt: 0.5 }}>
          <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>
            Breakdown
          </Typography>
          {displayItems.map((item: any, idx: number) => (
            <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.2 }}>
              <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 12, mr: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160 }}>
                {item.name}
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: 12, whiteSpace: 'nowrap' }}>
                ₹{Number(item.value).toLocaleString()}
              </Typography>
            </Box>
          ))}
          {remaining > 0 && (
            <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: 11, mt: 0.25, fontStyle: 'italic' }}>
              +{remaining} more items
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Chart component
// ---------------------------------------------------------------------------

interface RevenueByCategoryChartProps {
  data: CategoryDataItem[];
  serviceType: string;
}

const RevenueByCategoryChart: React.FC<RevenueByCategoryChartProps> = ({ data, serviceType }) => {
  const title =
    serviceType === 'room'
      ? 'Revenue by Room Type'
      : serviceType === 'food'
        ? 'Revenue by Category'
        : 'Revenue by Category / Room';

  return (
    <ChartCard title={title} timeout={1800}>
      <ResponsiveContainer width="100%" height="100%">
        <Treemap
          data={data}
          dataKey="value"
          nameKey="name"
          aspectRatio={4 / 3}
          content={<TreemapCustomContent />}
        >
          <RechartsTooltip content={<TreemapCustomTooltip />} cursor={false} />
        </Treemap>
      </ResponsiveContainer>
    </ChartCard>
  );
};

export default RevenueByCategoryChart;
